import * as fromRoot from "#core/router-store";
import * as assignmentsActions from "#core/teacher-classes/store/actions/assignments.action";
import * as gradeActions from "#core/teacher-classes/store/actions/grades.action";
import { SnackBarService } from "#services/snackbar.service";
import { Injectable } from "@angular/core";
import { Asset } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-angular-cure";
import { AssignmentsService, StudentsService } from "@carnegie-learning/data-access-angular-passport";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { PPAssignment as Assignment } from "cli-types";
import { of } from "rxjs";
import {
    catchError,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators";

@Injectable()
export class GradesEffects {
    constructor(
        private actions$: Actions,
        private store: Store<fromRoot.CustomSerializer>,
        private assignmentsService: AssignmentsService,
        private studentsService: StudentsService,
        private snackBarService: SnackBarService,
        private assetsService: AssetsService
    ) {}

    // Calls service to retrieve assignment data for a user based on assignmentID
    loadCurrentStudentAssignment$ = createEffect(() =>
        this.actions$
            .pipe(ofType(gradeActions.LoadCurrentStudentAssignment))
            .pipe(
                switchMap((action) => {
                    return this.assignmentsService
                        .getAssignment(action.assignmentID)
                        .pipe(
                            switchMap((response) => [
                                gradeActions.LoadCurrentStudentAssignmentSuccess(
                                    { payload: response }
                                ),
                                assignmentsActions.LoadAssignmentsCourseMetrics(
                                    {
                                        courseID: response.courseID,
                                        assetId: response.assetId,
                                        shortAssignableType:
                                            response.shortAssignableType,
                                    }
                                ),
                            ]),
                            catchError((error) =>
                                of(
                                    gradeActions.LoadCurrentStudentAssignmentFail(
                                        error
                                    )
                                )
                            )
                        );
                })
            )
    );

    LoadAllGradeAssignmentAssets$ = createEffect(() =>
        this.actions$
            .pipe(ofType(gradeActions.LoadAllGradeAssignmentAssets))
            .pipe(
                switchMap((action) => {
                    const assetIds = action.assignments
                        .map((a) => {
                            if (a.compositeTaskAssignments) {
                                const ids = a.compositeTaskAssignments.map(
                                    (t) => t.assetId
                                );
                                return [...ids, a.assetId];
                            } else return a.assetId;
                        })
                        .flat()
                        .filter((id) => id !== "");

                    const params: FeathersServicePaginationParameters<Asset> = {
                        limit: 100,
                        skip: 0,
                        filter: { "assetId[$in][]": assetIds },
                        sorting: {},
                    };
                    return this.assetsService.getAssets(params).pipe(
                        map((assets) =>
                            gradeActions.LoadAllGradeAssignmentAssetsSuccess({
                                assignments: action.assignments,
                                assets: assets.data,
                            })
                        ),
                        catchError((error) =>
                            of(
                                gradeActions.LoadAllGradeAssignmentAssetsFail(
                                    error
                                )
                            )
                        )
                    );
                })
            )
    );

    // Calls service to retrieve assignment data for a user based on assignmentID
    loadAssignmentList$ = createEffect(() =>
        this.actions$.pipe(ofType(gradeActions.LoadAssignmentList)).pipe(
            // get studentID from url
            withLatestFrom(
                this.store.pipe(select(fromRoot.getRouterState)),
                (action, router) => {
                    return {
                        payload: action.payload,
                        studentID: router.state.params.userID,
                        courseID: router.state.params.courseID,
                        assignmentID: router.state.params.assignmentID,
                        shortAssignableType: router
                            ? router.state.params.shortAssignableType ||
                              action.payload.shortAssignableType
                            : null,
                        assetId: router
                            ? router.state.params.assetId ||
                              action.payload.assetId
                            : null,
                    };
                }
            ),
            switchMap((params) => {
                return this.assignmentsService
                    .getCourseAssignmentStudents(
                        params.shortAssignableType,
                        params.assetId,
                        params.courseID,
                        params.payload
                    )
                    .pipe(
                        map((students) => students),
                        map((response) =>
                            gradeActions.LoadAssignmentListSuccess({
                                payload: response,
                            })
                        ),
                        catchError((error) =>
                            of(gradeActions.LoadAssignmentListFail(error))
                        )
                    );
            })
        )
    );

    // Calls service to update an assignment detail for a student not the class

    updateAssignment$ = createEffect(() =>
        this.actions$.pipe(ofType(gradeActions.UpdateStudentAssignment)).pipe(
            switchMap((action) => {
                return this.assignmentsService
                    .updateAssignment(action.params, action.assignmentID)
                    .pipe(
                        switchMap((response: Assignment) => {
                            return [
                                gradeActions.UpdateStudentAssignmentSuccess({
                                    payload: response,
                                    quickEval: action.quickEval,
                                }),
                                assignmentsActions.LoadAssignmentsCourseMetrics(
                                    {
                                        courseID: response.courseID,
                                        assetId: response.assetId,
                                        shortAssignableType:
                                            response.shortAssignableType,
                                    }
                                ),
                            ];
                        }),
                        tap((response) => {
                            this.snackBarService.success("Assignment Updated!");
                        }),
                        catchError((error) => {
                            this.snackBarService.error(
                                "Assignment Update Failed!"
                            );
                            return of(
                                gradeActions.UpdateStudentAssignmentFail(error)
                            );
                        })
                    );
            })
        )
    );
}
